import { useDispatch, useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useFormik } from "formik";
import {
  Divider,
  Typography,
  Stack,
  Checkbox,
  Button,
  FormControl,
  FormHelperText,
} from "@financeable-com-au/financeable-ui";
import {
  setSubmissionModalOpen,
  applicationSelector,
  setSubmissionSelectedDocuments,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type { ApplicationFormSlice, ApplicationState } from "@/store/slices/types/applicationFormSlice";
import { SubmissionStepper } from "@/components/SubmissionModal/SubmissionStepper";
import { useApplicationRouteType, useSubmissionSteps } from "@/components/SubmissionModal/hooks";
import { MiniLoader } from "@components/Loader";

const SelectDocuments = () => {
  // List of documents
  const { documents, customers } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const { proceedSubmissionSteps } = useSubmissionSteps();
  const applicationRouteType = useApplicationRouteType();
  const dispatch = useDispatch();

  const handleCloseSubmissionModal = () => {
    dispatch(setSubmissionModalOpen({ isOpen: false }));
  };

  const customerDocuments = (documents?.customer ?? []).map((document) => ({
    ...document,
    customerFirstName: customers.find((customer) => customer._id === document.customer)?.firstName ?? "",
    customerLastName: customers.find((customer) => customer._id === document.customer)?.lastName ?? "",
  }));

  const formik = useFormik({
    initialValues: {
      documents: [],
    },
    onSubmit: (values) => {
      dispatch(setSubmissionSelectedDocuments({ selectedDocuments: values.documents }));
      proceedSubmissionSteps({ activeStep: "documents" });
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  if (documents?.status === "pending") {
    return (
      <Stack padding={8}>
        <MiniLoader />
      </Stack>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack rowGap={10} padding={8}>
        <SubmissionStepper activeStep="documents" />
        <Stack rowGap={6}>
          {customerDocuments.length === 0 ? (
            <Stack rowGap={2}>
              <Typography component="h2" id="modal-title" level="title-md">
                There are no documents to select
              </Typography>
              <Typography id="document-checkboxes">
                Sorry, we couldn't find any documents uploaded for this {applicationRouteType}.
              </Typography>
            </Stack>
          ) : (
            <>
              <Stack rowGap={2}>
                <Typography component="h2" id="modal-title" level="title-md">
                  Select Documents
                </Typography>
                <Typography id="document-checkboxes">
                  Please select the documents to proceed with your {applicationRouteType}
                </Typography>
                <Divider />
              </Stack>
              <FormControl error={!!formik.errors.documents}>
                <Stack rowGap={3} role="group" aria-labelledby="document-checkboxes">
                  {customerDocuments.map(
                    ({ customerFirstName, customerLastName, name: documentName, _id: documentId }) => {
                      const label =
                        customerFirstName || customerLastName
                          ? `${customerFirstName} ${customerLastName}: ${documentName}`
                          : documentName;
                      return (
                        <Checkbox
                          name="documents"
                          label={label}
                          value={documentId}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          key={label}
                        />
                      );
                    },
                  )}
                  {!!formik.errors.documents && (
                    <FormHelperText margin={0}>
                      <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                      {formik.errors.documents}
                    </FormHelperText>
                  )}
                </Stack>
              </FormControl>
            </>
          )}
        </Stack>
      </Stack>
      <Stack
        columnGap={2}
        alignItems="center"
        bgcolor="neutral.100"
        borderTop="1px solid"
        borderColor="neutral.200"
        paddingX={8}
        paddingY={5}
        flexDirection="row"
      >
        <Button variant="secondary" onClick={handleCloseSubmissionModal} fullWidth type="button">
          Cancel
        </Button>
        <Button variant="primary" fullWidth type="submit">
          Proceed
        </Button>
      </Stack>
    </form>
  );
};

export { SelectDocuments };
